import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { Input, Button, Table } from 'antd'
import numeral from 'numeral'
import moment from 'moment'
import { getTeamUrl } from 'Common'
import { getSubOrders } from '../../../../HttpRequests/subOrders'
import SubOrderModal from '../../../../Modals/SubOrder'

const footerStyles = {
  footer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 25,
    borderTop: '1px solid #DDD'
  },
  total: {
    marginRight: 40
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: '#999'
  },
  value: {
    fontSize: 15,
    fontWeight: 500,
    color: '#444'
  }
}

const SubcontractorOrders = ({ projectId }) => {

  const [subOrders, setSubOrders] = useState([])
  const [fetching, setFetching] = useState(false)
  const [search, setSearch] = useState('')
  const [sorting, setSorting] = useState({ columnKey: 'date', descending: true })
  const [subOrderModal, setSubOrderModal] = useState({ show: false })

  const fetchData = async () => {
    setFetching(true)

    const params = {
      search: search || undefined,
      'filter[project_id]': projectId,
      sort: `${sorting.descending ? '-' : ''}${sorting.columnKey}`,
      include: [
        'subcontractor',
        'project.clientSite.client',
        'user',
      ].join(','),
    }

    try {
      const response = await getSubOrders({ params })
      setSubOrders(response.data || [])
    } catch (error) {
      console.error('Error fetching subcontractor orders:', error)
    } finally {
      setFetching(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [projectId, sorting])

  const onSearch = () => {
    fetchData()
  }

  const onChangeTable = (pagination, filters, sorter) => {
    if (sorter.columnKey) {
      setSorting({
        columnKey: sorter.columnKey,
        descending: sorter.order === 'descend',
      })
    }
  }

  const onClickNew = () => {
    setSubOrderModal({
      show: true,
      projectId: projectId,
    })
  }

  const onCloseSubOrderModal = () => {
    setSubOrderModal({ show: false })
    fetchData()
  }

  return (
    <React.Fragment>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>

        <div>
          <Input.Search
            value={search}
            onChange={e => setSearch(e.target.value)}
            onSearch={onSearch}
            placeholder='Search...'
            style={{ width: 250 }}
          />
        </div>

        {/* <Button type='primary' onClick={onClickNew}>New Order</Button> */}

      </div>

      <Table
        dataSource={subOrders}
        rowKey='id'
        onChange={onChangeTable}
        loading={fetching}
        scroll={{ x: 1200 }}
      >

        <Table.Column
          title='Reference'
          key='reference'
          sorter
          render={(text, record) => (
            <NavLink to={getTeamUrl(`sub_orders/${record.id}`)}>
              <div style={{ fontSize: 15 }}>{record.reference}</div>
            </NavLink>
          )}
        />

        <Table.Column
          title='Subcontractor'
          key='subcontractor_id'
          sorter
          render={(text, record) => (
            <React.Fragment>
              {record.subcontractor ? (
                <NavLink to={getTeamUrl(`subcontractors/${record.subcontractor.id}/view`)}>
                  <div style={{ fontSize: 15 }}>{record.subcontractor.name}</div>
                </NavLink>
              ) : (
                <div style={{ color: '#999' }}>-</div>
              )}
            </React.Fragment>
          )}
        />

        <Table.Column
          title='Date'
          key='date'
          sorter
          render={(text, record) => moment(record.date).format('Do MMM YYYY')}
        />

        <Table.Column
          title='Owner'
          key='user.name'
          sorter
          render={(text, record) => record.user ? record.user.full_name : '-'}
        />

        <Table.Column
          title='Set %'
          key='set_percentage'
          sorter
          onHeaderCell={() => ({
            style: { textAlign: 'right' },
          })}
          onCell={() => ({
            style: { textAlign: 'right' },
          })}
          render={(text, record) => `${numeral(record.set_percentage).format('0,0.00')}%`}
        />

        <Table.Column
          title='Subcontractor Total'
          key='set_amount'
          sorter
          onHeaderCell={() => ({
            style: { textAlign: 'right' },
          })}
          onCell={() => ({
            style: { textAlign: 'right' },
          })}
          render={(text, record) => `£${numeral(record.set_amount).format('0,0.00')}`}
        />

        <Table.Column
          title='Company Total'
          key='total'
          sorter
          onHeaderCell={() => ({
            style: { textAlign: 'right' },
          })}
          onCell={() => ({
            style: { textAlign: 'right' },
          })}
          render={(text, record) => `£${numeral(record.total).format('0,0.00')}`}
        />

      </Table>

      {subOrders.length > 0 && (
        <div style={footerStyles.footer}>
          <div style={footerStyles.total}>
            <div style={footerStyles.label}>Total Orders</div>
            <div style={footerStyles.value}>
              {subOrders.length}
            </div>
          </div>
          <div style={footerStyles.total}>
            <div style={footerStyles.label}>Subcontractors</div>
            <div style={footerStyles.value}>
              {new Set(subOrders.filter(o => o.subcontractor).map(o => o.subcontractor.id)).size}
            </div>
          </div>
          <div style={footerStyles.total}>
            <div style={footerStyles.label}>Company Total</div>
            <div style={footerStyles.value}>
              {`£${numeral(subOrders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0)).format('0,0.00')}`}
            </div>
          </div>
          <div style={footerStyles.total}>
            <div style={footerStyles.label}>Subcontractor Total</div>
            <div style={footerStyles.value}>
              {`£${numeral(subOrders.reduce((sum, order) => sum + parseFloat(order.set_amount || 0), 0)).format('0,0.00')}`}
            </div>
          </div>
        </div>
      )}

      {subOrderModal.show && (
        <SubOrderModal
          {...subOrderModal}
          onClose={onCloseSubOrderModal}
        />
      )}

    </React.Fragment>
  )
}

SubcontractorOrders.propTypes = {
  projectId: PropTypes.number.isRequired,
}

export default SubcontractorOrders
